/**
 * MobileAssistantHub — Phase 1 unified mobile SHS Assistant menu.
 * ────────────────────────────────────────────────────────────────
 * On mobile viewports (< sm ≈ 640px) only, this replaces the three
 * competing floating buttons (AI, Help, Upgrade) with a SINGLE
 * bottom-right trigger that opens a compact menu.
 *
 * The menu dispatches window CustomEvents so we don't have to lift
 * state out of the existing modal components:
 *   • 'shs:open-ai'   → AIHelper opens its chat panel
 *   • 'shs:open-help' → HelpCenterLite opens its help modal
 *   • Upgrade         → uses react-router navigation directly
 *
 * On ≥ sm the hub renders NOTHING. Desktop UX is unchanged.
 *
 * Auto-hides on the same routes as AIHelper / HelpCenterLite so it
 * never obscures camera capture or admin rapid-review workspaces.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Brain, HelpCircle, Sparkles, X, Menu } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const HIDE_ON_ROUTES = ['/cpap-fit', '/admin/media-deploy'];

const MobileAssistantHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);

  // Close menu on route change so it never lingers over new screens.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const hideOnRoute = HIDE_ON_ROUTES.some((p) => location.pathname.startsWith(p));

  const t = language === 'es'
    ? { title: 'Asistente SHS', ai: 'Preguntar a SHS-AI', help: 'Ayuda y soporte', upgrade: 'Mejorar plan', close: 'Cerrar' }
    : { title: 'SHS Assistant', ai: 'Ask SHS-AI', help: 'Help & support', upgrade: 'Upgrade plan', close: 'Close' };

  const dispatch = useCallback((name) => {
    try { window.dispatchEvent(new CustomEvent(name)); } catch { /* SSR / unsupported */ }
  }, []);

  const openAI = () => { setOpen(false); dispatch('shs:open-ai'); };
  const openHelp = () => { setOpen(false); dispatch('shs:open-help'); };
  const openUpgrade = () => { setOpen(false); navigate('/membership'); };

  if (hideOnRoute) return null;

  return (
    <>
      {/* Mobile-only trigger. Hidden ≥ sm so desktop keeps original layout.
          z-40 keeps it below the AI chat panel (z-50) and Help modal (z-50)
          so it never overlaps open panels; the opened menu still sits above
          via z-[70]. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="sm:hidden fixed bottom-4 right-4 z-40 h-12 w-12 rounded-full bg-gradient-to-br from-purple-600 to-indigo-700 text-white shadow-2xl flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2"
        style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label={open ? t.close : t.title}
        aria-expanded={open}
        aria-controls="mobile-assistant-menu"
        data-testid="mobile-assistant-trigger"
      >
        {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
      </button>

      {/* Popover menu — mobile only. */}
      {open && (
        <div
          id="mobile-assistant-menu"
          role="menu"
          aria-label={t.title}
          className="sm:hidden fixed bottom-20 right-4 z-[70] w-56 rounded-xl bg-white shadow-2xl ring-1 ring-gray-200 overflow-hidden"
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
          data-testid="mobile-assistant-menu"
        >
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-100">
            {t.title}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={openAI}
            className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
            data-testid="mobile-assistant-open-ai"
          >
            <span className="h-8 w-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
              <Brain className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="text-sm font-medium text-gray-900">{t.ai}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={openHelp}
            className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none border-t border-gray-100"
            data-testid="mobile-assistant-open-help"
          >
            <span className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <HelpCircle className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="text-sm font-medium text-gray-900">{t.help}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={openUpgrade}
            className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-amber-50 focus:bg-amber-50 focus:outline-none border-t border-gray-100"
            data-testid="mobile-assistant-open-upgrade"
          >
            <span className="h-8 w-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="text-sm font-medium text-gray-900">{t.upgrade}</span>
          </button>
        </div>
      )}
    </>
  );
};

export default MobileAssistantHub;
